import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sortable"
export default class extends Controller {
  static targets = ["item"]
  static values = { url: String }

  connect() {
    this.draggingId = null
    this.draggedEl = null
    this.placeholderEl = null
    this._rafPending = false
    this._latestClientY = null
    this._lastPlaceholderBeforeId = null

    // Handle dragover/drop on the container so dropping "between" items works.
    this._boundDragOver = this.dragOver.bind(this)
    this._boundDrop = this.drop.bind(this)
    this.element.addEventListener("dragover", this._boundDragOver)
    this.element.addEventListener("drop", this._boundDrop)
  }

  disconnect() {
    if (this._boundDragOver) this.element.removeEventListener("dragover", this._boundDragOver)
    if (this._boundDrop) this.element.removeEventListener("drop", this._boundDrop)
    this._boundDragOver = null
    this._boundDrop = null
  }

  dragStart(event) {
    const id = event.currentTarget.dataset.id
    this.draggingId = id
    this.draggedEl = event.currentTarget

    this.draggedEl.classList.add(
      "border-dashed",
      "opacity-60"
    )

    // Create placeholder up-front (stable element reduces jitter).
    if (!this.placeholderEl) {
      this.placeholderEl = document.createElement("li")
      this.placeholderEl.setAttribute("aria-hidden", "true")
      this.placeholderEl.dataset.sortablePlaceholder = "true"
      this.placeholderEl.style.pointerEvents = "none"
      this.placeholderEl.className =
        "rounded-2xl border-2 border-dashed border-gray-900 bg-transparent shadow-none"
    }
    this.placeholderEl.style.height = `${this.draggedEl.offsetHeight}px`
    this._lastPlaceholderBeforeId = null

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", id)
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    if (!this.draggingId) return
    this._latestClientY = event.clientY

    if (this._rafPending) return
    this._rafPending = true
    requestAnimationFrame(() => {
      this._rafPending = false
      this._updatePlaceholderPosition()
    })
  }

  _updatePlaceholderPosition() {
    if (!this.draggingId || !this.draggedEl || !this.placeholderEl) return
    const parent = this.element
    if (!parent) return

    const y = this._latestClientY
    if (typeof y !== "number") return

    // Compute insertion point purely from Y vs item midpoints.
    const candidates = this.itemTargets.filter((el) => el !== this.draggedEl)
    let beforeEl = null
    for (const el of candidates) {
      const rect = el.getBoundingClientRect()
      const mid = rect.top + rect.height / 2
      if (y < mid) {
        beforeEl = el
        break
      }
    }

    const beforeId = beforeEl ? beforeEl.dataset.id : "__end__"
    if (beforeId === this._lastPlaceholderBeforeId) return
    this._lastPlaceholderBeforeId = beforeId

    if (!parent.contains(this.placeholderEl)) {
      parent.insertBefore(this.placeholderEl, beforeEl)
      return
    }

    // Move placeholder only when necessary (prevents jitter).
    if (beforeEl) {
      parent.insertBefore(this.placeholderEl, beforeEl)
    } else {
      parent.appendChild(this.placeholderEl)
    }
  }

  drop(event) {
    event.preventDefault()
    const draggedId = this.draggingId || event.dataTransfer.getData("text/plain")
    if (!draggedId) return

    const dragged = this.itemTargets.find((el) => el.dataset.id === draggedId)
    if (!dragged) return

    if (this.placeholderEl && this.placeholderEl.parentElement) {
      this.placeholderEl.parentElement.insertBefore(dragged, this.placeholderEl)
      this.placeholderEl.remove()
    }

    this.submit()
  }

  dragEnd(_event) {
    this.draggingId = null
    this._latestClientY = null
    this._lastPlaceholderBeforeId = null
    if (this.draggedEl) {
      this.draggedEl.classList.remove("border-dashed", "opacity-60")
    }
    this.draggedEl = null

    if (this.placeholderEl) {
      this.placeholderEl.remove()
    }
  }

  async submit() {
    if (!this.hasUrlValue) return

    const token = document.head.querySelector("meta[name=csrf-token]")?.content
    const order = this.itemTargets.map((el) => Number(el.dataset.id))

    await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        "Accept": "application/json",
      },
      body: JSON.stringify({ order }),
      credentials: "same-origin",
    })
  }
}
